import React, { useState } from 'react';
import { BeachData } from '../types';

interface BeachCardProps {
  beachData: BeachData;
}

const BeachCard: React.FC<BeachCardProps> = ({ beachData }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { beach, distance, driveTime, currentWeather, weatherScore, scores } = beachData;

  const getScoreClass = (score: number): string => {
    if (score >= 80) return 'score-excellent';
    if (score >= 60) return 'score-good';
    return 'score-poor';
  };

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getWindDirection = (direction: string): string => {
    const directions: { [key: string]: string } = {
      'N': 'North',
      'NE': 'Northeast',
      'E': 'East',
      'SE': 'Southeast',
      'S': 'South',
      'SW': 'Southwest',
      'W': 'West',
      'NW': 'Northwest'
    };
    return directions[direction] || direction;
  };

  return (
    <div className="beach-card" onClick={() => setIsExpanded(!isExpanded)}>
      <div className="beach-card-header">
        <div className="beach-info">
          <div className="beach-name">{beach.name}</div>
          <div className="distance">
            📍 {distance.toFixed(1)} miles • 🚗 {formatTime(driveTime)}
          </div>
          {!isExpanded && (
            <div className="expand-indicator">
              Click to see weather details ↓
            </div>
          )}
        </div>
        <div className="scores-container">
          <div className={`score-item ${getScoreClass(scores?.arrivalScore || weatherScore)}`}>
            <div className="score-label">Arrival</div>
            <div className="score-value">{scores?.arrivalScore || weatherScore}</div>
          </div>
          <div className={`score-item ${getScoreClass(scores?.oneHourScore || weatherScore)}`}>
            <div className="score-label">+1h</div>
            <div className="score-value">{scores?.oneHourScore || weatherScore}</div>
          </div>
          <div className={`score-item ${getScoreClass(scores?.threeHourScore || weatherScore)}`}>
            <div className="score-label">+3h</div>
            <div className="score-value">{scores?.threeHourScore || weatherScore}</div>
          </div>
        </div>
      </div>

      <div className={`beach-details ${!isExpanded ? 'collapsed' : ''}`}>
        <div className="weather-details">
          <div className="weather-item">
            <span>🌡️ Temperature:</span>
            <span>{currentWeather.temperature}°F</span>
          </div>
          <div className="weather-item">
            <span>💨 Wind:</span>
            <span>{currentWeather.windSpeed} mph {getWindDirection(currentWeather.windDirection)}</span>
          </div>
          <div className="weather-item">
            <span>🌧️ Precipitation:</span>
            <span>{(currentWeather.precipitation * 100).toFixed(0)}%</span>
          </div>
          <div className="weather-item">
            <span>☁️ Cloud Cover:</span>
            <span>{currentWeather.cloudCover}%</span>
          </div>
        </div>

        <div style={{ marginTop: '15px', fontSize: '0.9em', color: '#7f8c8d' }}>
          <strong>2-Hour Forecast:</strong>
          {currentWeather.forecast.slice(0, 2).map((hour, index) => (
            <div key={index} style={{ padding: '5px 0', borderBottom: '1px solid #eee' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{new Date(hour.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                <span>{hour.temperature}°F, {hour.windSpeed}mph, {(hour.precipitation * 100).toFixed(0)}% rain</span>
              </div>
            </div>
          ))}
        </div>

        <div className="expand-indicator" style={{ textAlign: 'center', marginTop: '10px' }}>
          Click to collapse ↑
        </div>
      </div>
    </div>
  );
};

export default BeachCard;